/*  BlogAuthor — /creator/blog
    ────────────────────────────────────────────────────────────────
    iter100.154 — Certification-Author blog authoring workbench,
    parity with `/creator/courses`. Two views:
      • List    — own posts + status pills + review history badges
      • Editor  — same markdown-source + live-preview experience as
                  the admin manager, but limited to draft/rejected
                  posts. Publishing is replaced with "Submit for review"
                  which flips the post to `under_review` and drops a
                  row on the admin review inbox.

    State machine mirrors the Course flow:
      draft ─submit─▶ under_review ─approve─▶ published
                                   └─reject─▶ rejected ─edit─▶ draft
*/
import React, { useEffect, useMemo, useState, useRef } from "react";
import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";
import { toast } from "sonner";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import {
    Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import {
    Plus, MagnifyingGlass, PencilSimple, Archive, ArrowClockwise,
    CheckCircle, ArrowLeft, FloppyDisk, PaperPlaneTilt,
    Eye, Image as ImageIcon, X, Sparkle, ClockClockwise,
    ChatCircleDots, WarningCircle,
} from "@phosphor-icons/react";
import SeoPanel from "@/components/creator/SeoPanel";


/* ── Empty shell for a new post ────────────────────────────────── */
const EMPTY_POST = {
    id: null,
    title: "",
    slug: "",
    excerpt: "",
    content_md: "",
    hero_image_url: "",
    hero_image_alt: "",
    category: "smile-science",
    audience: "patient",
    author_name: "",
    author_role: "",
    author_avatar_url: "",
    status: "draft",
    seo_title: "",
    seo_description: "",
    // iter100.157s — Tier 3 SEO fields.
    meta_title: "",
    meta_description: "",
    focus_keyword: "",
    tags: [],
    og_title: "",
    og_description: "",
};


export default function BlogAuthor() {
    const [view, setView] = useState("list");
    const [selectedId, setSelectedId] = useState(null);
    const [refreshKey, setRefreshKey] = useState(0);

    const openEditor = (id) => { setSelectedId(id); setView("editor"); };
    const backToList = () => {
        setSelectedId(null);
        setView("list");
        setRefreshKey((k) => k + 1);
    };

    return (
        <div className="max-w-6xl mx-auto p-5 sm:p-8" data-testid="author-blog-manager">
            {view === "list" ? (
                <BlogList key={refreshKey} onOpen={openEditor} onNew={() => openEditor(null)} />
            ) : (
                <BlogEditor postId={selectedId} onBack={backToList} />
            )}
        </div>
    );
}


/* ================================================================
   List view
   ================================================================ */
function BlogList({ onOpen, onNew }) {
    const [items, setItems] = useState([]);
    const [loading, setLoading] = useState(true);
    const [statusFilter, setStatusFilter] = useState("all");
    const [q, setQ] = useState("");

    const load = async () => {
        setLoading(true);
        try {
            const params = statusFilter !== "all" ? { status: statusFilter } : {};
            const { data } = await api.get("/blog-builder/posts", { params });
            setItems(data.items || []);
        } catch {
            toast.error("Failed to load your posts");
        } finally {
            setLoading(false);
        }
    };
    useEffect(() => { load(); }, [statusFilter]);

    const filtered = useMemo(() => {
        const term = q.trim().toLowerCase();
        if (!term) return items;
        return items.filter((p) => (p.title || "").toLowerCase().includes(term));
    }, [items, q]);

    return (
        <div className="space-y-6">
            <div className="flex flex-wrap items-center justify-between gap-3">
                <div>
                    <h1 className="font-display text-2xl sm:text-3xl font-bold tracking-tight" data-testid="author-blog-title">
                        My Blog Posts
                    </h1>
                    <p className="text-sm text-muted-foreground mt-1">
                        Write, save drafts, and submit for admin approval. Published posts are live on{" "}
                        <a href="/blog" target="_blank" rel="noopener noreferrer" className="text-primary underline underline-offset-2">/blog</a>.
                    </p>
                </div>
                <Button onClick={onNew} className="rounded-full h-11 px-5 bg-primary text-primary-foreground" data-testid="author-blog-new">
                    <Plus size={16} weight="bold" className="mr-2" /> New Post
                </Button>
            </div>

            <div className="flex flex-wrap items-center gap-2">
                <div className="relative flex-1 max-w-sm">
                    <div className="absolute inset-y-0 left-3 flex items-center pointer-events-none">
                        <MagnifyingGlass size={16} className="text-muted-foreground" />
                    </div>
                    <Input
                        value={q}
                        onChange={(e) => setQ(e.target.value)}
                        placeholder="Search your posts…"
                        className="h-10 pl-9 rounded-full bg-white"
                        data-testid="author-blog-search"
                    />
                </div>
                <div className="inline-flex bg-secondary/70 rounded-full p-1 flex-wrap" data-testid="author-blog-status-tabs">
                    {[
                        { id: "all",           label: "All" },
                        { id: "draft",         label: "Drafts" },
                        { id: "under_review",  label: "Under review" },
                        { id: "published",     label: "Published" },
                        { id: "rejected",      label: "Rejected" },
                        { id: "archived",      label: "Archived" },
                    ].map((s) => (
                        <button
                            key={s.id}
                            type="button"
                            onClick={() => setStatusFilter(s.id)}
                            className={`h-8 px-3.5 rounded-full text-[11px] font-bold tracking-wide uppercase transition-all ${
                                statusFilter === s.id
                                    ? "bg-primary text-primary-foreground shadow-sm"
                                    : "text-foreground/60 hover:text-foreground"
                            }`}
                            data-testid={`author-blog-status-${s.id}`}
                        >
                            {s.label}
                        </button>
                    ))}
                </div>
            </div>

            <div className="bg-card border border-border rounded-2xl overflow-hidden bento-shadow">
                {loading ? (
                    <div className="p-8 text-center text-muted-foreground text-sm">Loading…</div>
                ) : filtered.length === 0 ? (
                    <div className="p-12 text-center">
                        <Sparkle size={32} weight="duotone" className="mx-auto text-primary/40 mb-3" />
                        <div className="font-semibold">
                            {statusFilter === "all" ? "No posts yet" : `No posts in "${statusFilter.replace("_", " ")}"`}
                        </div>
                        <p className="text-sm text-muted-foreground mt-1">Click New Post to start writing.</p>
                    </div>
                ) : (
                    <div className="divide-y divide-border" data-testid="author-blog-list">
                        {filtered.map((p) => (
                            <PostRow key={p.id} post={p} onOpen={onOpen} />
                        ))}
                    </div>
                )}
            </div>
        </div>
    );
}


function PostRow({ post, onOpen }) {
    return (
        <button
            type="button"
            onClick={() => onOpen(post.id)}
            data-testid={`author-blog-row-${post.slug}`}
            className="w-full text-left px-5 py-4 hover:bg-secondary/30 transition-colors flex items-center gap-4"
        >
            <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2 flex-wrap">
                    <span className="font-display font-semibold text-base truncate">{post.title || "Untitled draft"}</span>
                    <StatusPill status={post.status} />
                </div>
                <div className="text-xs text-muted-foreground mt-1 flex items-center gap-3 flex-wrap">
                    <span>{post.category}</span>
                    <span>·</span>
                    <span className="capitalize">{post.audience}</span>
                    <span>·</span>
                    <span>{post.reading_time_min || 1} min read</span>
                    <span>·</span>
                    <span>Updated {post.updated_at ? new Date(post.updated_at).toLocaleDateString() : "—"}</span>
                </div>
            </div>
            <PencilSimple size={16} className="text-muted-foreground shrink-0" />
        </button>
    );
}


function StatusPill({ status }) {
    const map = {
        published:    { icon: CheckCircle,   tone: "bg-emerald-100 text-emerald-700",   label: "Published" },
        draft:        { icon: PencilSimple,  tone: "bg-secondary text-foreground/70",   label: "Draft" },
        under_review: { icon: ClockClockwise,tone: "bg-amber-100 text-amber-800",       label: "Under review" },
        rejected:     { icon: WarningCircle, tone: "bg-rose-100 text-rose-700",         label: "Needs edits" },
        archived:     { icon: Archive,       tone: "bg-secondary text-muted-foreground",label: "Archived" },
    };
    const cfg = map[status] || map.draft;
    const Icon = cfg.icon;
    return (
        <span className={`inline-flex items-center gap-1 text-[10px] font-bold uppercase tracking-wider px-2 py-0.5 rounded-full ${cfg.tone}`}>
            <Icon size={10} weight="fill" />
            {cfg.label}
        </span>
    );
}


/* ================================================================
   Editor view
   ================================================================ */
function BlogEditor({ postId, onBack }) {
    const [form, setForm] = useState(EMPTY_POST);
    const [loading, setLoading] = useState(Boolean(postId));
    const [saving, setSaving] = useState(false);
    const [reviews, setReviews] = useState([]);
    const [showMediaPicker, setShowMediaPicker] = useState(false);
    const [showSubmit, setShowSubmit] = useState(false);
    // iter100.157t — Full-width markdown editor with a Preview toggle
    // (replaces the old side-by-side split view).
    const [previewMode, setPreviewMode] = useState(false);

    const isEditable = form.status === "draft" || form.status === "rejected" || !form.id;

    useEffect(() => {
        if (!postId) return;
        (async () => {
            try {
                const { data } = await api.get(`/blog-builder/posts/${postId}`);
                setForm({ ...EMPTY_POST, ...data });
                const reviewResp = await api.get(`/blog-builder/posts/${postId}/reviews`);
                setReviews(reviewResp.data.reviews || []);
            } catch {
                toast.error("Failed to load post");
                onBack();
            } finally {
                setLoading(false);
            }
        })();
    }, [postId]);

    const update = (patch) => setForm((f) => ({ ...f, ...patch }));

    const handleSave = async () => {
        if (!form.title.trim()) {
            toast.error("Add a title before saving");
            return;
        }
        setSaving(true);
        try {
            const payload = {
                title: form.title,
                excerpt: form.excerpt,
                content_md: form.content_md,
                category: form.category,
                audience: form.audience,
                hero_image_url: form.hero_image_url,
                hero_image_alt: form.hero_image_alt,
                author_name: form.author_name,
                author_role: form.author_role,
                author_avatar_url: form.author_avatar_url,
                seo_title: form.seo_title,
                seo_description: form.seo_description,
                // iter100.157s — Tier 3 SEO fields.
                meta_title: form.meta_title,
                meta_description: form.meta_description,
                focus_keyword: form.focus_keyword,
                tags: form.tags,
                og_title: form.og_title,
                og_description: form.og_description,
            };
            if (form.id) {
                await api.patch(`/blog-builder/posts/${form.id}`, { ...payload, slug: form.slug });
                toast.success("Draft saved");
            } else {
                const { data } = await api.post("/blog-builder/posts", payload);
                setForm({ ...EMPTY_POST, ...data });
                toast.success("Draft created");
            }
            onBack();
        } catch (err) {
            toast.error(err.response?.data?.detail || "Save failed");
        } finally {
            setSaving(false);
        }
    };

    const handleDelete = async () => {
        if (!form.id) { onBack(); return; }
        if (!window.confirm("Delete this draft? It will be archived and hidden from your list.")) return;
        try {
            await api.delete(`/blog-builder/posts/${form.id}`);
            toast.success("Post archived");
            onBack();
        } catch (err) {
            toast.error(err.response?.data?.detail || "Delete failed");
        }
    };

    if (loading) {
        return <div className="p-12 text-center text-muted-foreground">Loading post…</div>;
    }

    const latestReview = reviews.length > 0 ? reviews[0] : null;

    return (
        <div className="space-y-6" data-testid="author-blog-editor">
            {/* Top bar */}
            <div className="flex flex-wrap items-center justify-between gap-3">
                <button
                    type="button"
                    onClick={onBack}
                    className="inline-flex items-center gap-1.5 text-sm font-semibold text-muted-foreground hover:text-primary"
                    data-testid="author-blog-back"
                >
                    <ArrowLeft size={14} weight="bold" />
                    Back to my posts
                </button>
                <div className="flex items-center gap-2 flex-wrap">
                    {form.id && <StatusPill status={form.status} />}
                    {form.id && isEditable && (
                        <Button
                            variant="outline"
                            onClick={handleDelete}
                            className="rounded-full h-10 px-4 text-rose-600 border-rose-200 hover:bg-rose-50"
                            data-testid="author-blog-delete"
                        >
                            <Archive size={14} className="mr-1.5" />
                            Archive
                        </Button>
                    )}
                    {isEditable && (
                        <Button
                            variant="outline"
                            onClick={handleSave}
                            disabled={saving}
                            className="rounded-full h-10 px-4"
                            data-testid="author-blog-save"
                        >
                            <FloppyDisk size={14} weight="bold" className="mr-1.5" />
                            Save draft
                        </Button>
                    )}
                    {isEditable && form.id && (
                        <Button
                            onClick={() => setShowSubmit(true)}
                            disabled={saving}
                            className="rounded-full h-10 px-5 bg-primary text-primary-foreground"
                            data-testid="author-blog-submit"
                        >
                            <PaperPlaneTilt size={14} weight="fill" className="mr-1.5" />
                            Submit for review
                        </Button>
                    )}
                </div>
            </div>

            {/* Read-only banner for under_review / published */}
            {!isEditable && form.id && (
                <div className="rounded-2xl border border-border p-4 bg-secondary/40 flex items-start gap-3" data-testid="author-blog-readonly-banner">
                    <ClockClockwise size={18} weight="fill" className="text-primary shrink-0 mt-0.5" />
                    <div className="text-sm">
                        <div className="font-semibold">
                            {form.status === "under_review"
                                ? "This post is with the admin for review."
                                : form.status === "published"
                                    ? "This post is live on /blog."
                                    : "This post is locked."}
                        </div>
                        <div className="text-muted-foreground text-xs mt-1">
                            You can view but not edit. {form.status === "under_review"
                                ? "You'll see approval or feedback here once decided."
                                : form.status === "published"
                                    ? "Ask an admin if you need to make changes."
                                    : ""}
                        </div>
                    </div>
                </div>
            )}

            {/* Rejection feedback callout */}
            {form.status === "rejected" && latestReview?.feedback && (
                <div className="rounded-2xl border border-rose-200 bg-rose-50/60 p-4" data-testid="author-blog-rejection-feedback">
                    <div className="flex items-start gap-3">
                        <ChatCircleDots size={18} weight="fill" className="text-rose-600 shrink-0 mt-0.5" />
                        <div className="flex-1">
                            <div className="text-[10px] uppercase tracking-[0.16em] font-bold text-rose-700 mb-1">Admin feedback</div>
                            <p className="text-sm text-foreground/90 leading-relaxed whitespace-pre-wrap">{latestReview.feedback}</p>
                        </div>
                    </div>
                </div>
            )}

            {/* Editor */}
            <div className="grid lg:grid-cols-[1fr_320px] gap-6">
                <div className="space-y-4">
                    <Input
                        value={form.title}
                        onChange={(e) => update({ title: e.target.value })}
                        placeholder="Article title"
                        disabled={!isEditable}
                        className="h-14 text-2xl font-display font-bold border-0 border-b-2 border-border rounded-none bg-transparent px-0 focus-visible:ring-0 focus-visible:border-primary disabled:opacity-70"
                        data-testid="author-blog-input-title"
                    />
                    <Textarea
                        value={form.excerpt}
                        onChange={(e) => update({ excerpt: e.target.value })}
                        placeholder="Excerpt (shown on cards) · 1–2 sentences"
                        rows={2}
                        disabled={!isEditable}
                        className="text-base bg-white border-border rounded-xl disabled:opacity-70"
                        data-testid="author-blog-input-excerpt"
                    />

                    <div>
                        <div className="flex items-center justify-between mb-2">
                            <div className="text-[10px] uppercase tracking-[0.16em] font-bold text-muted-foreground flex items-center gap-1.5">
                                {previewMode ? "Live preview" : "Markdown source"}
                            </div>
                            <button
                                type="button"
                                onClick={() => setPreviewMode(!previewMode)}
                                className="inline-flex items-center gap-1 text-[11px] font-semibold text-muted-foreground hover:text-foreground bg-white border border-border rounded-lg px-2.5 py-1 transition-colors"
                                data-testid="author-blog-preview-toggle"
                            >
                                {previewMode ? (
                                    <><PencilSimple size={11} weight="bold" /> Edit</>
                                ) : (
                                    <><Eye size={11} weight="bold" /> Preview</>
                                )}
                            </button>
                        </div>
                        {previewMode ? (
                            <div className="bg-white border border-border rounded-xl p-6 min-h-[500px]" data-testid="author-blog-preview">
                                <article className="prose-invisique">
                                    <ReactMarkdown remarkPlugins={[remarkGfm]}>
                                        {form.content_md || "*Nothing yet — start typing.*"}
                                    </ReactMarkdown>
                                </article>
                            </div>
                        ) : (
                            <Textarea
                                value={form.content_md}
                                onChange={(e) => update({ content_md: e.target.value })}
                                placeholder="Write your article in Markdown…&#10;&#10;## Heading&#10;Paragraph text.&#10;- List item&#10;&#10;> Blockquote"
                                rows={22}
                                disabled={!isEditable}
                                className="font-mono text-[13px] leading-relaxed bg-white border-border rounded-xl min-h-[500px] disabled:opacity-70"
                                data-testid="author-blog-input-content"
                            />
                        )}
                    </div>
                </div>

                {/* Sidebar */}
                <div className="space-y-5">
                    <Panel title="Hero image">
                        {form.hero_image_url ? (
                            <div className="space-y-3">
                                <div className="relative rounded-xl overflow-hidden bg-secondary">
                                    <img src={form.hero_image_url} alt="Hero" className="w-full h-32 object-cover" />
                                    {isEditable && (
                                        <button
                                            type="button"
                                            onClick={() => update({ hero_image_url: "", hero_image_alt: "" })}
                                            className="absolute top-2 right-2 w-7 h-7 rounded-full bg-white/90 grid place-items-center shadow"
                                            aria-label="Remove"
                                        >
                                            <X size={13} weight="bold" />
                                        </button>
                                    )}
                                </div>
                                <div className="flex items-start gap-2">
                                    <Input
                                        value={form.hero_image_alt}
                                        onChange={(e) => update({ hero_image_alt: e.target.value })}
                                        placeholder="Alt text — describes the image for search + a11y"
                                        disabled={!isEditable}
                                        className="text-xs h-9 disabled:opacity-70 flex-1"
                                    />
                                    <Button
                                        type="button"
                                        size="sm"
                                        variant="outline"
                                        onClick={async () => {
                                            if (!form.id) {
                                                toast.error("Save the draft first, then generate alt-text.");
                                                return;
                                            }
                                            try {
                                                const { data } = await api.post(`/blog-builder/posts/${form.id}/seo/generate-alt`);
                                                update({ hero_image_alt: data.alt_text });
                                                toast.success("Alt-text generated.");
                                            } catch (e) {
                                                toast.error(e?.response?.data?.detail || "Couldn't generate alt-text.");
                                            }
                                        }}
                                        disabled={!isEditable || !form.id}
                                        className="h-9 px-2.5 text-[11px] font-semibold shrink-0"
                                        title="Generate a descriptive alt-text with Gemini vision"
                                        data-testid="author-blog-gen-alt-btn"
                                    >
                                        <Sparkle size={12} weight="fill" className="mr-1" />
                                        AI alt
                                    </Button>
                                </div>
                            </div>
                        ) : (
                            <button
                                type="button"
                                onClick={() => setShowMediaPicker(true)}
                                disabled={!isEditable}
                                className="w-full h-32 rounded-xl border-2 border-dashed border-border bg-secondary/40 hover:border-primary hover:bg-primary/5 transition-colors grid place-items-center text-muted-foreground hover:text-primary disabled:opacity-50 disabled:cursor-not-allowed"
                                data-testid="author-blog-pick-hero"
                            >
                                <div className="text-center">
                                    <ImageIcon size={22} className="mx-auto mb-1" />
                                    <div className="text-xs font-semibold">Pick from Media Library</div>
                                </div>
                            </button>
                        )}
                    </Panel>

                    <Panel title="Category & audience">
                        <div className="space-y-3">
                            <div>
                                <Label className="text-[10px] uppercase tracking-wider text-muted-foreground">Category</Label>
                                <Select value={form.category} onValueChange={(v) => update({ category: v })} disabled={!isEditable}>
                                    <SelectTrigger className="mt-1 h-10 rounded-lg" data-testid="author-blog-select-category"><SelectValue /></SelectTrigger>
                                    <SelectContent>
                                        <SelectItem value="smile-science">Smile Science</SelectItem>
                                        <SelectItem value="patient-stories">Patient Stories</SelectItem>
                                        <SelectItem value="clinical-insights">Clinical Insights</SelectItem>
                                        <SelectItem value="product-deep-dives">Product Deep-Dives</SelectItem>
                                        <SelectItem value="industry-news">Industry & News</SelectItem>
                                        <SelectItem value="aftercare">Aftercare</SelectItem>
                                    </SelectContent>
                                </Select>
                            </div>
                            <div>
                                <Label className="text-[10px] uppercase tracking-wider text-muted-foreground">Audience</Label>
                                <Select value={form.audience} onValueChange={(v) => update({ audience: v })} disabled={!isEditable}>
                                    <SelectTrigger className="mt-1 h-10 rounded-lg" data-testid="author-blog-select-audience"><SelectValue /></SelectTrigger>
                                    <SelectContent>
                                        <SelectItem value="patient">For Patients</SelectItem>
                                        <SelectItem value="professional">For Professionals</SelectItem>
                                    </SelectContent>
                                </Select>
                            </div>
                        </div>
                    </Panel>

                    <Panel title="URL slug">
                        <Input
                            value={form.slug}
                            onChange={(e) => update({ slug: e.target.value })}
                            placeholder="auto-generated from title"
                            disabled={!isEditable}
                            className="h-9 text-sm font-mono disabled:opacity-70"
                            data-testid="author-blog-input-slug"
                        />
                        <p className="text-[11px] text-muted-foreground mt-2">
                            /blog/<span className="font-mono">{form.slug || "auto-generated"}</span>
                        </p>
                    </Panel>

                    {reviews.length > 0 && (
                        <Panel title="Review history">
                            <div className="space-y-2.5" data-testid="author-blog-review-history">
                                {reviews.map((r) => (
                                    <ReviewEntry key={r.id} review={r} />
                                ))}
                            </div>
                        </Panel>
                    )}
                </div>
            </div>

            {/* iter100.157u — SEO Ranking Machine, promoted below the editor
                as a full-width, prominent stage so it isn't buried in the
                narrow right sidebar. */}
            <SeoPanel
                form={form}
                update={update}
                isEditable={isEditable}
                onGeneratedRefresh={async () => {
                    // Server-side generate already persisted the new
                    // fields; nothing else to do here.
                }}
                layout="wide"
            />

            {showMediaPicker && (
                <MediaPicker
                    onClose={() => setShowMediaPicker(false)}
                    onPick={(m) => {
                        update({
                            hero_image_url: m.url,
                            hero_image_alt: m.alt || form.hero_image_alt || form.title,
                        });
                        setShowMediaPicker(false);
                    }}
                />
            )}

            {showSubmit && (
                <SubmitModal
                    onClose={() => setShowSubmit(false)}
                    onConfirm={async (note) => {
                        try {
                            // Save any pending edits first so the reviewer sees the latest.
                            const payload = {
                                title: form.title,
                                excerpt: form.excerpt,
                                content_md: form.content_md,
                                category: form.category,
                                audience: form.audience,
                                hero_image_url: form.hero_image_url,
                                hero_image_alt: form.hero_image_alt,
                                author_name: form.author_name,
                                author_role: form.author_role,
                                author_avatar_url: form.author_avatar_url,
                                seo_title: form.seo_title,
                                seo_description: form.seo_description,
                                // iter100.157s — Tier 3 SEO fields.
                                meta_title: form.meta_title,
                                meta_description: form.meta_description,
                                focus_keyword: form.focus_keyword,
                                tags: form.tags,
                                og_title: form.og_title,
                                og_description: form.og_description,
                                slug: form.slug,
                            };
                            await api.patch(`/blog-builder/posts/${form.id}`, payload);
                            await api.post(`/blog-builder/posts/${form.id}/submit`, { note });
                            toast.success("Sent to admin for review");
                            setShowSubmit(false);
                            onBack();
                        } catch (err) {
                            toast.error(err.response?.data?.detail || "Could not submit for review");
                        }
                    }}
                />
            )}
        </div>
    );
}


function ReviewEntry({ review }) {
    const decided = review.decision;
    const decidedTone = decided === "approve"
        ? "text-emerald-700 bg-emerald-50"
        : decided === "reject"
            ? "text-rose-700 bg-rose-50"
            : "text-amber-700 bg-amber-50";
    const decidedLabel = decided === "approve" ? "Approved" : decided === "reject" ? "Rejected" : "Pending";
    return (
        <div className="border border-border rounded-xl p-3 bg-secondary/20">
            <div className="flex items-center justify-between text-[11px] font-semibold">
                <span>Submitted {new Date(review.submitted_at).toLocaleDateString()}</span>
                <span className={`px-2 py-0.5 rounded-full text-[10px] font-bold uppercase tracking-wider ${decidedTone}`}>{decidedLabel}</span>
            </div>
            {review.submitter_note && (
                <div className="mt-2 text-xs italic text-muted-foreground">&ldquo;{review.submitter_note}&rdquo;</div>
            )}
            {review.feedback && (
                <div className="mt-2 text-xs">
                    <div className="text-[10px] uppercase tracking-widest font-bold text-muted-foreground mb-1">Feedback</div>
                    <div className="text-foreground/90 whitespace-pre-wrap leading-relaxed">{review.feedback}</div>
                </div>
            )}
        </div>
    );
}


function Panel({ title, children }) {
    return (
        <div className="bg-card border border-border rounded-2xl p-4">
            <div className="text-[10px] uppercase tracking-[0.18em] font-bold text-muted-foreground mb-3">{title}</div>
            {children}
        </div>
    );
}


/* ────────────────────────────────────────────────────────────── */
function SubmitModal({ onClose, onConfirm }) {
    const [note, setNote] = useState("");
    const [busy, setBusy] = useState(false);
    return (
        <div className="fixed inset-0 z-50 bg-foreground/60 backdrop-blur-sm grid place-items-center p-4">
            <div className="bg-card rounded-3xl w-full max-w-md p-6 shadow-2xl" data-testid="author-blog-submit-modal">
                <div className="flex items-start gap-3 mb-5">
                    <div className="w-11 h-11 rounded-full bg-primary/10 grid place-items-center">
                        <PaperPlaneTilt size={20} weight="fill" className="text-primary" />
                    </div>
                    <div>
                        <div className="font-display font-bold text-lg leading-tight">Submit for admin review</div>
                        <div className="text-xs text-muted-foreground mt-1">Once submitted, you won&apos;t be able to edit until the admin decides.</div>
                    </div>
                </div>
                <Label className="text-[10px] uppercase tracking-wider font-bold text-muted-foreground">Note for the reviewer (optional)</Label>
                <Textarea
                    value={note}
                    onChange={(e) => setNote(e.target.value)}
                    placeholder="Anything the reviewer should know? e.g. 'follow-up to our patient story series'."
                    rows={3}
                    className="mt-1"
                    data-testid="author-blog-submit-note"
                />
                <div className="flex gap-2 mt-5 justify-end">
                    <Button variant="outline" onClick={onClose} className="rounded-full">Cancel</Button>
                    <Button
                        onClick={async () => { setBusy(true); await onConfirm(note); setBusy(false); }}
                        disabled={busy}
                        className="rounded-full bg-primary text-primary-foreground"
                        data-testid="author-blog-submit-confirm"
                    >
                        <PaperPlaneTilt size={13} weight="fill" className="mr-1.5" />
                        Send for review
                    </Button>
                </div>
            </div>
        </div>
    );
}


function MediaPicker({ onClose, onPick }) {
    const [items, setItems] = useState([]);
    const [loading, setLoading] = useState(true);
    const [q, setQ] = useState("");
    const [uploading, setUploading] = useState(false);
    const fileInputRef = useRef(null);

    const load = async () => {
        setLoading(true);
        try {
            const { data } = await api.get("/admin/media?limit=200");
            setItems(data.items || []);
        } catch (err) {
            toast.error(err?.response?.data?.detail || "Couldn't load the media library.");
        } finally {
            setLoading(false);
        }
    };
    useEffect(() => { load(); }, []);

    const handleUpload = async (e) => {
        const file = e.target.files?.[0];
        if (!file) return;
        // Client-side pre-checks — keep in sync with backend limits.
        if (!/^image\/(jpeg|jpg|png|webp|gif)$/i.test(file.type)) {
            toast.error("Only JPEG, PNG, WebP or GIF images are allowed.");
            return;
        }
        if (file.size > 10 * 1024 * 1024) {
            toast.error("Image is too large — keep it under 10MB.");
            return;
        }
        setUploading(true);
        try {
            const form = new FormData();
            form.append("file", file);
            form.append("category", "blog-hero");
            form.append("alt", "");
            const { data } = await api.post("/admin/media/upload", form, {
                headers: { "Content-Type": "multipart/form-data" },
            });
            toast.success("Image uploaded — picking it now.");
            onPick(data);   // auto-select the freshly uploaded asset
        } catch (err) {
            toast.error(err?.response?.data?.detail || "Upload failed.");
        } finally {
            setUploading(false);
            if (fileInputRef.current) fileInputRef.current.value = "";
        }
    };

    const filtered = useMemo(() => {
        const term = q.trim().toLowerCase();
        if (!term) return items;
        return items.filter((m) => (m.original_filename || "").toLowerCase().includes(term));
    }, [items, q]);
    return (
        <div className="fixed inset-0 z-50 bg-foreground/60 backdrop-blur-sm grid place-items-center p-4">
            <div className="bg-card rounded-3xl w-full max-w-4xl max-h-[85vh] overflow-hidden flex flex-col shadow-2xl">
                <div className="flex items-center justify-between p-5 border-b border-border">
                    <div>
                        <div className="font-display font-bold text-lg">Pick or upload a hero image</div>
                        <div className="text-xs text-muted-foreground">{items.length} media assets in library</div>
                    </div>
                    <div className="flex items-center gap-2">
                        <input
                            ref={fileInputRef}
                            type="file"
                            accept="image/png,image/jpeg,image/webp,image/gif"
                            onChange={handleUpload}
                            className="hidden"
                            data-testid="author-blog-media-upload-input"
                        />
                        <Button
                            type="button"
                            onClick={() => fileInputRef.current?.click()}
                            disabled={uploading}
                            className="h-9 px-4 rounded-full bg-primary text-primary-foreground text-xs font-semibold"
                            data-testid="author-blog-media-upload-btn"
                        >
                            {uploading ? "Uploading…" : (<><Plus size={13} weight="bold" className="mr-1.5" />Upload new</>)}
                        </Button>
                        <button
                            type="button"
                            onClick={onClose}
                            className="w-9 h-9 rounded-full grid place-items-center hover:bg-secondary"
                            aria-label="Close"
                            data-testid="author-blog-media-close"
                        >
                            <X size={16} weight="bold" />
                        </button>
                    </div>
                </div>
                <div className="p-4 border-b border-border">
                    <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search by filename…" className="h-10" />
                </div>
                <div className="flex-1 overflow-auto p-4">
                    {loading ? <div className="text-center text-muted-foreground py-12">Loading…</div> : filtered.length === 0 ? (
                        <div className="text-center py-14">
                            <ImageIcon size={36} className="mx-auto text-primary/40 mb-2" />
                            <div className="font-semibold text-sm">
                                {q ? "No matches for that search." : "No images in your library yet."}
                            </div>
                            <p className="text-xs text-muted-foreground mt-1">
                                {q ? "Try a different keyword or upload a new image above." : "Click 'Upload new' above to add your first image."}
                            </p>
                        </div>
                    ) : (
                        <div className="grid grid-cols-3 sm:grid-cols-4 lg:grid-cols-5 gap-3">
                            {filtered.map((m) => (
                                <button key={m.id} type="button" onClick={() => onPick(m)} className="group relative aspect-square rounded-xl overflow-hidden bg-secondary border border-border hover:border-primary transition-all" title={m.original_filename}>
                                    <img src={m.url} alt={m.alt || m.original_filename} className="w-full h-full object-cover" loading="lazy" />
                                    <div className="absolute inset-0 bg-primary/60 opacity-0 group-hover:opacity-100 transition-opacity grid place-items-center">
                                        <CheckCircle size={22} weight="fill" className="text-white" />
                                    </div>
                                </button>
                            ))}
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
}
